// client/src/components/pages/LexiStar/StatusBoard/StatusBoardPhone.tsx
//
// The status board on a phone (ST-00207, m4b-contracts §5): one card per truck instead of the
// table row — registration and fleet number, data age, the two-axis status pill (with its
// overlays and "reminded ×N"), driver and place, the trip (from → to, reference), distance on
// route with ETA, fuel with the burn index — and a chevron: the card opens the trip. The run chip
// and Override keep their own 44 px taps. "By status" (the Kanban view) is the same cards under
// one heading per operational context, top to bottom instead of side-scrolling columns.

import type { ReactNode } from 'react';
import { FaHandPaper } from 'react-icons/fa';
import PhoneCard, { PhoneCardList, type PhoneCardTone } from '../../../common/PhoneCard';
import {
  StatusPill,
  DataAge,
  RunChip,
  BurnIndex,
  AckRemindersTag,
  AwaitingReportTag,
  type RunSummary,
} from './atoms';
import {
  DistanceCell,
  FuelUsedCell,
  EtaCell,
  locationText,
  rowDriver,
  rowRegistration,
  rowUpdatedAt,
} from './columns';
import { burnIndexBand } from './fuelLogic';
import { CONTEXT_GROUPS as CONTEXT_GROUPS_JS, contextGroupOf as contextGroupOfJs } from './statusLanguage';
import { ASSET_STATUS_CLASSES, type AssetStatusKey } from '../../../map/assetStatus';
import { statusKeyOfRow } from './controlRoom';
import type { StatusBoardRow } from '../../../../types/lexistar-status-board';

interface ContextGroup {
  id: string;
  label: string;
  contexts: string[];
  tone: AssetStatusKey;
}
const CONTEXT_GROUPS = CONTEXT_GROUPS_JS as ContextGroup[];
const contextGroupOf = contextGroupOfJs as (context: string | null | undefined) => ContextGroup;

/** The desktop table's row accent (burn index, dwell breach) as the card's left edge. */
const toneOf = (row: StatusBoardRow): PhoneCardTone | null => {
  const band: string = burnIndexBand(row.fuel?.burn_index ?? null, row.trip?.pct_distance ?? null);
  if (band === 'critical') return 'critical';
  if (band === 'warning') return 'warning';
  if (row.dwell_breach) return 'notice';
  return null;
};

export interface PhoneStatusCardsProps {
  rows: StatusBoardRow[];
  now: number;
  canOverride: boolean;
  onOverride: (row: StatusBoardRow) => void;
  onOpenRun: (run: RunSummary) => void;
  highlighted: Set<string>;
}

function StatusCard({
  row,
  now,
  canOverride,
  onOverride,
  onOpenRun,
  highlighted,
}: Omit<PhoneStatusCardsProps, 'rows'> & { row: StatusBoardRow }) {
  const reg = rowRegistration(row);
  const place = locationText(row);
  const trip = row.trip;
  const actions: ReactNode[] = [];
  if (row.run)
    actions.push(<RunChip key="run" run={row.run} onOpen={onOpenRun} className="touch-target justify-center" />);
  if (canOverride && trip)
    actions.push(
      <button
        key="override"
        type="button"
        onClick={() => onOverride(row)}
        aria-label={`Override ${reg}`}
        className="touch-target inline-flex items-center justify-center gap-1.5 px-3 text-sm font-medium bg-surface border border-slate-300 text-slate-700 rounded-xl"
      >
        <FaHandPaper size={11} aria-hidden="true" /> Override
      </button>
    );
  return (
    <PhoneCard
      title={
        <>
          <span className="font-mono">{reg}</span>
          {row.fleet_no && <span className="ml-1.5 text-xs font-medium text-slate-500 font-mono">{row.fleet_no}</span>}
        </>
      }
      to={trip ? `/lexistar/trips/${trip.id}` : undefined}
      openLabel={trip ? `${reg}: open trip ${trip.reference}` : undefined}
      aside={<DataAge ts={rowUpdatedAt(row)} now={now} />}
      tone={toneOf(row)}
      highlight={highlighted.has(row.asset_key)}
      actions={actions.length ? actions : null}
      dataAttributes={{ 'data-asset': row.asset_key }}
    >
      <div className="flex flex-wrap items-center gap-1">
        <StatusPill status={row.status} statusKey={statusKeyOfRow(row, now)} display={row.display} />
        <AckRemindersTag trip={trip} />
        <AwaitingReportTag trip={trip} />
      </div>
      <div className="text-slate-600">
        {rowDriver(row) || 'No driver'}
        {place && ` · ${place}`}
      </div>
      {trip ? (
        <>
          <div className="text-slate-800">
            {trip.from_name} → {trip.to_name}{' '}
            <span className="text-xs text-slate-500 font-mono whitespace-nowrap">{trip.reference}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <DistanceCell trip={trip} />
            {trip.eta && (
              <span className="text-sm text-slate-700 whitespace-nowrap">
                ETA <EtaCell trip={trip} />
              </span>
            )}
          </div>
          {row.fuel && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-xs text-slate-500">Fuel</span>
              <FuelUsedCell fuel={row.fuel} />
              <span className="text-xs text-slate-500">burn</span>
              <BurnIndex value={row.fuel.burn_index} pctDistance={trip.pct_distance} />
            </div>
          )}
        </>
      ) : row.next_trip ? (
        <div className="text-slate-600">
          Next: {row.next_trip.from_name} → {row.next_trip.to_name}{' '}
          <span className="text-xs text-slate-500 font-mono">{row.next_trip.reference} · planned</span>
        </div>
      ) : (
        <div className="text-xs text-slate-400 italic">No active trip</div>
      )}
    </PhoneCard>
  );
}

/** One card per truck, in the board's order. */
export function PhoneStatusCards({ rows, ...rest }: PhoneStatusCardsProps) {
  return (
    <PhoneCardList label="Trucks">
      {rows.map((r) => (
        <StatusCard key={r.asset_key} row={r} {...rest} />
      ))}
    </PhoneCardList>
  );
}

/** "By status": the cards under one heading per operational context (the Kanban columns). */
export function PhoneStatusGroups({ rows, ...rest }: PhoneStatusCardsProps) {
  const byGroup = new Map<string, StatusBoardRow[]>(CONTEXT_GROUPS.map((g) => [g.id, []]));
  rows.forEach((r) => byGroup.get(contextGroupOf(r.status?.context).id)?.push(r));
  const groups = CONTEXT_GROUPS.filter((g) => (byGroup.get(g.id) ?? []).length > 0);
  return (
    <div>
      {groups.map((g) => {
        const inGroup = byGroup.get(g.id) ?? [];
        return (
          <section key={g.id} className="phone-card-group" aria-label={`${g.label} (${inGroup.length})`}>
            <h2 className="phone-card-group__heading">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${ASSET_STATUS_CLASSES[g.tone].dot}`} aria-hidden="true" />
              {g.label}
              <span className="tabular-nums">{inGroup.length}</span>
            </h2>
            <PhoneCardList label={g.label}>
              {inGroup.map((r) => (
                <StatusCard key={r.asset_key} row={r} {...rest} />
              ))}
            </PhoneCardList>
          </section>
        );
      })}
    </div>
  );
}
